/* eslint-disable react/prop-types */
import React, { useState, useContext } from "react";
import axios from "axios";
import Swal from "sweetalert2";

// Celdas personalizadas
import DefaultCell from "layouts/pages/orders/order-list/components/DefaultCell";
import CustomerCell from "layouts/pages/orders/order-list/components/CustomerCell";
import AccionesCell from "layouts/pages/orders/order-list/components/AccionesCell";
import StatusCell from "layouts/pages/orders/order-list/components/StatusCell";
import SoftSelect from "components/SoftSelect";

import { Box } from "@mui/material";

// Auth
import { AuthContext } from "context/AuthContext";

// Iconos
import DraftsIcon from "@mui/icons-material/Drafts";
import EmailIcon from "@mui/icons-material/Email";
import FolderDeleteIcon from "@mui/icons-material/FolderDelete";

// Toast
const Toast = Swal.mixin({
  toast: true,
  position: "top-end",
  showConfirmButton: false,
  timer: 3000,
  timerProgressBar: true,
  didOpen: (toast) => {
    toast.onmouseenter = Swal.stopTimer;
    toast.onmouseleave = Swal.resumeTimer;
  },
});

// Mapeo de status a sus propiedades
const statusMapping = {
  "En Proceso": { icon: "schedule", color: "info", id: 1 },
  Recibida: { icon: "done", color: "success", id: 2 },
  Enviada: { icon: "send", color: "warning", id: 3 },
  Entregada: { icon: "done", color: "primary", id: 4 },
  Anulada: { icon: "close", color: "error", id: 5 },
};

// Opciones del select
const statusOptions = Object.entries(statusMapping).map(
  ([statusName, { icon, color, id }]) => ({
    value: id,
    label: <StatusCell icon={icon} color={color} status={statusName} size="sm" />,
  })
);

/** Select que actualiza el estado via PATCH */
function StatusSelect({ orderId, currentStatus }) {
  const initialId = statusMapping[currentStatus]?.id || null;
  const [statusId, setStatusId] = useState(initialId);

  const handleChange = async (selectedOption) => {
    const newStatusId = selectedOption.value;
    setStatusId(newStatusId);

    try {
      await axios.patch(`${process.env.REACT_APP_SERVER_URL}/api/orders/${orderId}/`, {
        status: newStatusId,
      });

      const statusName = selectedOption.label.props.status;
      Toast.fire({ icon: "success", title: `Estado actualizado a: ${statusName}` });
    } catch (error) {
      console.error("Error actualizando el estado:", error);
      Toast.fire({ icon: "error", title: "Ocurrió un error al actualizar el estado" });
    }
  };

  return (
    <SoftSelect
      options={statusOptions}
      value={statusOptions.find((opt) => opt.value === statusId)}
      onChange={handleChange}
      menuPortalTarget={document.body}
      placeholder="Seleccione estado"
    />
  );
}

/** 🔒 Renderer de celda que decide según el rol:
 *  - client  => muestra texto + ícono
 *  - admin/user => muestra el select editable
 */
function StatusCellRenderer({ row, value }) {
  const { hasRole } = useContext(AuthContext);
  const isClient = hasRole("client");

  if (isClient) {
    const s = value; // string del estado
    const map = statusMapping[s] || {};
    return <StatusCell icon={map.icon} color={map.color} status={s} size="sm" />;
  }

  return <StatusSelect orderId={row.original.id} currentStatus={value} />;
}

/** Obtiene la lista de órdenes y columnas */
const fetchData = async () => {
  try {
    const response = await axios.get(`${process.env.REACT_APP_SERVER_URL}/api/orders/`);
    const orders = response.data;

    const rows = orders.map((order) => ({
      id: order.id_orden_cabecera,
      numero_orden_cabecera: order.numero_orden_cabecera,
      nombre_estado: order.nombre_estado,
      id_estado: order.id_estado,
      cliente: [order.cliente, { image: order.cliente.charAt(0) }],
      origen: order.origen,
      incoterms: order.incoterms,
      fechaEntrega: order.fecha_entrega,
      fechaRealEntrega: order.fecha_real,
      status: order.status, // <- string del estado que usa StatusCellRenderer
      acciones: (
        <AccionesCell
          numeroOrdenCabecera={order.id_orden_cabecera}
          nombreEstado={order.nombre_estado}
        />
      ),
    }));

    return {
      columns: [
        {
          Header: "No Orden",
          accessor: "numero_orden_cabecera",
          Cell: ({ row }) => {
            const nombreEstado = row.original.nombre_estado;
            const numeroOrden = row.original.numero_orden_cabecera;

            switch (nombreEstado) {
              case "APERTURADA":
                return (
                  <Box style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
                    <DraftsIcon />
                    <span>{numeroOrden}</span>
                  </Box>
                );
              case "FINALIZADA":
                return (
                  <Box style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
                    <EmailIcon />
                    <span>{numeroOrden}</span>
                  </Box>
                );
              case "Anulada":
                return (
                  <Box style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
                    <FolderDeleteIcon />
                    <span>{numeroOrden}</span>
                  </Box>
                );
              default:
                return <span>{numeroOrden}</span>;
            }
          },
        },
        {
          Header: "cliente",
          accessor: "cliente",
          Cell: ({ value: [name, data] }) => (
            <CustomerCell image={data.image} color={data.color || "dark"} name={name} />
          ),
        },
        {
          Header: "estado",
          accessor: "status",
          Cell: ({ row, value }) => <StatusCellRenderer row={row} value={value} />,
        },
        {
          Header: "origen",
          accessor: "origen",
          Cell: ({ value }) => <DefaultCell value={value} />,
        },
        {
          Header: "incoterms",
          accessor: "incoterms",
          Cell: ({ value }) => <DefaultCell value={value} />,
        },
        {
          Header: "fecha de entrega",
          accessor: "fechaEntrega",
          Cell: ({ value }) => <DefaultCell value={value} />,
        },
        {
          Header: "fecha real de entrega",
          accessor: "fechaRealEntrega",
          Cell: ({ value }) => <DefaultCell value={value} />,
        },
        {
          Header: "Acciones",
          accessor: "acciones",
          Cell: ({ row }) => row.original.acciones,
        },
      ],
      rows,
    };
  } catch (error) {
    console.error("Error fetching orders data:", error);
    return { columns: [], rows: [] };
  }
};

export default fetchData;
